import Modal, { ModalTransition, ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';

import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import useCreatePost, { type PostFormData } from 'hooks/ai-forums/useCreatePost';
import Form, { FormApi } from '@atlaskit/form';
import { QuestionFormInputs } from './QuestionFormInputs';
import { useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
}

function AskQuestionModal({ open, onClose }: Props) {
  const { createPost, isPending } = useCreatePost();
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (data: PostFormData, form: FormApi<PostFormData>) => {
    try {
      await createPost(data, {
        onSuccess: (post) => {
          form.reset();
          onClose();
          window.location.reload();
        },
        onError: (errors) => {
          console.error('Failed to create post:', errors);

          setErrorMessage(errors?.[0] ?? 'An unknown error occurred');
          // Show a toast notification here
          // or handle errors in a different way
        },
      });
    } catch (validationErrors) {
      return validationErrors;
    }
  };

  return (
    <ModalTransition>
      {open && (
        <Modal onClose={onClose}>
          <Form<PostFormData> onSubmit={handleSubmit}>
            {({ formProps }) => (
              <form {...formProps} noValidate>
                <ModalHeader>
                  <ModalTitle>Ask a question</ModalTitle>
                </ModalHeader>
                <ModalBody>
                  <QuestionFormInputs />
                  {errorMessage ? <div css={errorBannerStyle}>{errorMessage}</div> : null}
                  <div css={infoBannerStyle}>
                    <strong>The Atlassian Community is open to the public</strong>
                    <br />
                    Content posted here can appear in search engines. Please take care not to post sensitive or
                    personally identifiable information or any other internal company information that you would not
                    want to share publicly.
                  </div>
                </ModalBody>
                <ModalFooter>
                  <Button appearance="subtle" onClick={onClose} isDisabled={isPending}>
                    Cancel
                  </Button>
                  <Button appearance="primary" type="submit" isDisabled={isPending}>
                    {isPending ? 'Publishing...' : 'Publish'}
                  </Button>
                </ModalFooter>
              </form>
            )}
          </Form>
        </Modal>
      )}
    </ModalTransition>
  );
}

const infoBannerStyle = css({
  background: token('color.background.information'),
  color: token('color.text.information'),
  borderRadius: 4,
  padding: 12,
  margin: '16px 0',
  fontSize: 14,
});

const errorBannerStyle = css({
  background: token('color.background.danger'),
  color: token('color.text.danger'),
  borderRadius: 4,
  padding: 12,
  margin: '16px 0',
  fontSize: 14,
});

export default AskQuestionModal;
